import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { Editor, Transforms, Text } from 'slate';
import { uid } from 'react-uid';
import { ReactEditor } from 'slate-react';

import Leaf from './Leaf';
import Element from './Element';

const LIST_TYPES = ['numbered-list', 'bulleted-list', 'unordered-list', 'ordered-list'];
const BLOCK_FORMATS = [
	'heading-one',
	'heading-two',
	'heading-three',
	'heading-four',
	'block-quote',
	'numbered-list',
	'bulleted-list',
	'ordered-list',
	'unordered-list',
	'indent',
	'outdent'
];

export const HOTKEYS = {
	'mod+b': 'bold',
	'mod+i': 'italic',
	'mod+u': 'underline',
	'mod+`': 'code'
};

export const isBlockActive = (editor, format) => {
	// eslint-disable-next-line no-unused-vars,no-restricted-syntax
	for (const [_] of Editor.nodes(editor, {
		match: n => n.type === format
	})) {
		return true;
	}
	return false;
};

export const isMarkActive = (editor, format) => {
	const marks = Editor.marks(editor);
	return marks ? marks[format] === true : false;
};

export const toggleBlock = (editor, format) => {
	const isActive = isBlockActive(editor, format);
	const isList = LIST_TYPES.includes(format);
	Transforms.unwrapNodes(editor, {
		match: n => LIST_TYPES.includes(n.type),
		split: true
	});

	Transforms.setNodes(editor, {
		// eslint-disable-next-line no-nested-ternary
		type: isActive ? 'paragraph' : isList ? 'list-item' : format
	});

	if (!isActive && isList) {
		const block = { type: format, children: [] };
		Transforms.wrapNodes(editor, block);
	}
};

export const toggleMark = (editor, format) => {
	const isActive = isMarkActive(editor, format);

	if (isActive) {
		Editor.removeMark(editor, format);
	} else {
		Editor.addMark(editor, format, true);
	}
};

const mapLeaf = leaf => {
	const marks =
		leaf.marks?.reduce((a, c) => {
			a[c.type] = true;
			return a;
		}, {}) || {};
	return {
		text: leaf.text,
		...marks
	};
};

function migrateNode(node) {
	if (node.object === 'text') {
		return mapLeaf(node);
	}
	if (node.type === 'list-item') {
		return {
			type: node.type,
			children: node.nodes.reduce((a, c) => a.concat(c.nodes.map(migrateNode)), [])
		};
	}
	return {
		type: node.type,
		children: node.nodes.map(migrateNode)
	};
}

export const migrateSlateDataIfNeeded = data => {
	if (data?.document?.nodes) {
		try {
			return data.document.nodes.map(migrateNode);
		} catch (err) {
			console.log(data, err);
		}
	}
	try {
		return data.filter(item => item.children);
	} catch (e) {
		return data;
	}
};

export const isEmptySlate = node => {
	if (node) {
		const isEmpty =
			JSON.stringify(node) ===
			JSON.stringify([
				{
					type: 'paragraph',
					children: [
						{
							text: ''
						}
					]
				}
			]);
		return isEmpty;
	}

	return true;
};

const serializeHtmlNode = node => {
	const { attributes = {}, text, children = [] } = node;
	if (Text.isText(node)) {
		return (
			<Leaf key={uid(node)} leaf={node} attributes={attributes}>
				{text}
			</Leaf>
		);
	}
	const childrenEl = children.map(n => serializeHtmlNode(n));
	return (
		<Element key={uid(node)} element={node} attributes={attributes}>
			{childrenEl}
		</Element>
	);
};

export const htmlSerializer = node => {
	if (!node || !Array.isArray(node)) {
		return '';
	}
	return ReactDOMServer.renderToStaticMarkup(node.map(serializeHtmlNode));
};

export const isBlockFormat = format => BLOCK_FORMATS.includes(format);

export const handleCompositionEnd = (editor, lastComposeData, event) => {
	const { current } = lastComposeData;
	if (current) {
		// Store the current selection so we can move back once we have finished applying queued changes
		// eslint-disable-next-line max-len
		// Convert to slate range straight away as the selection can get messed up due to element fixing code
		const {
			anchorNode,
			anchorOffset,
			focusNode,
			focusOffset,
			isCollapsed
		} = window.getSelection();

		// Apply each of the changes
		// eslint-disable-next-line no-restricted-syntax
		for (const c of current) {
			const { selection, node, elementNode } = c;
			Transforms.select(editor, selection);
			Editor.insertText(editor, c.value);

			// If the text was completely removed it can clear the slate element
			// which will cause a slate crash (Chrome)
			if (!c.value) {
				const textNode = node.parentElement.closest('[data-slate-node="text"]');
				const stringNode = node.parentElement;
				if (
					elementNode &&
					textNode &&
					stringNode &&
					elementNode.children.length === 1 &&
					elementNode.children[0].nodeName === 'BR'
				) {
					stringNode.innerText = '';
					elementNode.replaceChild(textNode, elementNode.children[0]);
				}
			}
		}

		lastComposeData.current = null;

		// Move back to existing selection
		try {
			const ss = ReactEditor.toSlateRange(editor, {
				startContainer: anchorNode,
				startOffset: anchorOffset,
				endContainer: focusNode,
				endOffset: focusOffset,
				collapsed: isCollapsed
			});
			// Small fixup again
			ss.anchor.offset = anchorOffset;
			ss.focus.offset = focusOffset;
			Transforms.select(editor, ss);

			// Reset the dom selection as well... this can get out of alignment
			const sel = window.getSelection();
			sel.removeAllRanges();
			const newDomRange = ReactEditor.toDOMRange(editor, ss);
			if (newDomRange) {
				sel.addRange(newDomRange);
			}
		} catch (err) {
			console.log(err);
		}
	}

	// Prevent slate from doing anything
	// This prevents the hack that is currently in onCompositionEnd that doesn't work
	event.data = null;
};

export const handleDOMBeforeInput = (editor, lastComposeData, event) => {
	switch (event.inputType) {
		case 'insertCompositionText': {
			// We need to push each composition event so we can apply it when finished
			if (!lastComposeData.current) {
				lastComposeData.current = [];
			}
			try {
				// We need to convert the selection right away, as the window.getSelection() will change with the window
				const selection = window.getSelection();
				const sel = ReactEditor.toSlateRange(editor, selection);

				// The offsets may not match because the composition text might be far ahead etc...
				sel.anchor.offset = selection.anchorOffset;
				sel.focus.offset = selection.focusOffset;

				lastComposeData.current.push({
					selection: sel,
					value: event.data,
					node: selection?.anchorNode,
					elementNode: selection?.anchorNode?.parentElement.closest(
						'[data-slate-node="element"]'
					)
				});
			} catch {
				lastComposeData.current = null;
			}
			break;
		}
		case 'insertFromComposition':
		case 'deleteByComposition':
			// If we get this event we don't need to apply any sort of fix, this is the correct event to handle things
			lastComposeData.current = null;
			break;
		default:
			break;
	}
};
